/* Dashboard tab — the 30-second business snapshot.
 * KPI cards + best-ROI callout + editable business assumptions. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, DollarSign, Wallet, Trophy, Target, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { downloadAnnualReport } from "./reports";

const fmtUSD = (n) => n == null ? "—" : `$${(+n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => n == null ? "—" : (+n).toLocaleString(undefined, { maximumFractionDigits: 1 });

export default function DashboardTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [avgFee, setAvgFee] = useState(0);
  const [margin, setMargin] = useState(0);
  const [target, setTarget] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/command-center/dashboard");
      setData(data);
      setAvgFee(data.assumptions?.avg_assignment_fee || 0);
      setMargin(data.assumptions?.profit_margin_pct || 0);
      setTarget(data.assumptions?.target_closings_per_month || 0);
    } catch {
      toast.error("Could not load dashboard.");
    }
  };

  useEffect(() => { load(); }, []);

  const saveAssumptions = async () => {
    setBusy(true);
    try {
      await api.patch("/command-center/assumptions", {
        avg_assignment_fee: +avgFee,
        profit_margin_pct: +margin,
        target_closings_per_month: +target,
      });
      toast.success("Assumptions saved.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="card p-6 text-center text-[var(--ink-3)]" data-testid="dashboard-loading">Loading…</div>;

  const p = data.projected;
  const hitTarget = target > 0 && p.closings_per_month >= target;

  return (
    <div className="space-y-6" data-testid="command-center-dashboard-tab">
      {/* Empty prompt */}
      {data.counts.lead_sources === 0 && (
        <div className="banner-info" data-testid="dashboard-empty-prompt">
          You haven&apos;t added any lead sources yet. Head to the <strong>Lead Planner</strong> tab to enter
          your first source, or hit <strong>Load Tracy&apos;s template</strong> above to seed sensible defaults.
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-kpi-grid">
        <KpiCard icon={TrendingUp} label="Projected closings / month" value={fmtNum(p.closings_per_month)}
                 accent="#0EA5E9" testId="kpi-closings"
                 sub={hitTarget ? `On pace vs. target of ${fmtNum(target)}` : `Target: ${fmtNum(target)}`}/>
        <KpiCard icon={DollarSign} label="Projected gross / month" value={fmtUSD(p.monthly_gross)}
                 accent="#10B981" testId="kpi-gross"
                 sub={`${fmtNum(margin)}% margin assumption`} />
        <KpiCard icon={Wallet} label="Lead budget / month" value={fmtUSD(p.monthly_lead_budget)}
                 accent="#F97316" testId="kpi-budget"
                 sub={`Op-ex: ${fmtUSD(p.monthly_opex)}`} />
        <KpiCard icon={Target} label="Projected NET / month" value={fmtUSD(p.monthly_net)}
                 accent="#F59E0B" testId="kpi-net"
                 sub={p.monthly_net > 0 ? "After lead spend + op-ex" : "Losing money — tighten your funnel"} />
      </div>

      {/* Best-ROI callout */}
      {data.best_source && (
        <div className="card" data-testid="dashboard-best-source">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "rgba(230, 167, 62, 0.15)", color: "#F59E0B" }}>
              <Trophy size={20} />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow" style={{ color: "#F59E0B" }}>Best-ROI Lead Source</div>
              <div className="font-display font-bold text-xl mt-1" data-testid="best-source-name">
                {data.best_source.name}
              </div>
              <div className="text-[var(--ink-2)] text-sm mt-1">
                <strong>{fmtUSD(data.best_source.cost_per_deal)}</strong> cost per closed deal —
                lowest of your active sources.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actuals this month vs. projection */}
      <div className="card" data-testid="dashboard-actuals">
        <div className="label-eyebrow mb-3">This month · from your CRM</div>
        <div className="grid grid-cols-3 gap-4">
          <ActualMini label="Leads generated" value={data.actuals_this_month.leads_generated} testId="actual-leads" />
          <ActualMini label="Contracts signed" value={data.actuals_this_month.contracts_signed} testId="actual-contracts" />
          <ActualMini label="Deals closed" value={data.actuals_this_month.deals_closed} testId="actual-closed" />
        </div>
        <p className="text-xs text-[var(--ink-3)] mt-3 italic">
          These auto-populate from your CRM in real time. Use the Monthly Tracker tab to override.
        </p>
      </div>

      {/* Assumptions */}
      <div className="card" data-testid="dashboard-assumptions">
        <div className="label-eyebrow mb-3">Business assumptions</div>
        <div className="grid md:grid-cols-3 gap-4">
          <label className="text-sm block">
            <span className="block mb-1 text-[var(--ink-2)]">Avg. assignment fee ($)</span>
            <input type="number" className="form-input" value={avgFee}
                   onChange={(e) => setAvgFee(e.target.value)}
                   data-testid="assumptions-avg-fee" min={0} step={100} />
          </label>
          <label className="text-sm block">
            <span className="block mb-1 text-[var(--ink-2)]">Profit margin %</span>
            <input type="number" className="form-input" value={margin}
                   onChange={(e) => setMargin(e.target.value)}
                   data-testid="assumptions-margin" min={0} max={100} step={1} />
          </label>
          <label className="text-sm block">
            <span className="block mb-1 text-[var(--ink-2)]">Target closings / month</span>
            <input type="number" className="form-input" value={target}
                   onChange={(e) => setTarget(e.target.value)}
                   data-testid="assumptions-target" min={0} step={1} />
          </label>
        </div>
        <button type="button" onClick={saveAssumptions} disabled={busy}
                className="btn-money mt-4" data-testid="assumptions-save">
          {busy ? "Saving…" : "Save assumptions"}
        </button>
      </div>

      {/* Downloadable Annual Business Report — the "hand this to your CPA
          or lender" moment. Uses the current year so operators mid-year
          get a partial view; use the year picker on Monthly Tracker to
          download prior years. */}
      <div className="card" data-testid="dashboard-report-cta">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
               style={{ background: "rgba(11, 29, 59, 0.08)", color: "#0B1D3B" }}>
            <FileText size={20} />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="label-eyebrow">Downloadable Report</div>
            <div className="font-display font-bold text-lg mt-1">Annual Business Report (PDF)</div>
            <div className="text-[var(--ink-2)] text-sm mt-1">
              A branded, one-shot document combining your projections, lead planner, expenses,
              and monthly P&amp;L. Perfect for your CPA, tax filing, or a loan application.
            </div>
          </div>
          <button type="button"
                  onClick={() => downloadAnnualReport(new Date().getFullYear())}
                  className="btn-money" data-testid="dashboard-download-pdf">
            <FileText size={14} /> Download for {new Date().getFullYear()}
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div className="card" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
             style={{ background: `${accent}22`, color: accent }}>
          <Icon size={16} />
        </div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-bold">{label}</div>
      </div>
      <div className="font-display font-black text-2xl tabular-nums" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs text-[var(--ink-3)] mt-1">{sub}</div>}
    </div>
  );
}

function ActualMini({ label, value, testId }) {
  return (
    <div className="text-center" data-testid={testId}>
      <div className="font-display font-black text-2xl tabular-nums">{value ?? 0}</div>
      <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)] font-semibold mt-1">{label}</div>
    </div>
  );
}
